import { isEmpty, set } from 'lodash'
import { useMemo } from 'react'
import { adapters } from 'utils/documents'
import useStoreSelector from 'utils/hooks/useStoreSelector'
import { selectUserId, subUserId } from '../selectors'
import { userPreferencesStore } from 'services/state'
import { HIDDEN_COLUMNS } from '../constants'
import { updateUserDataPreferences } from 'services/api/user'

const getStorage = ({ kind }) => {
  const userPref = userPreferencesStore.get() || {}
  const dataPref = userPref?.[kind] || {}
  return dataPref?.[HIDDEN_COLUMNS] || {}
}

const makeColumnVisibilityHandler =
  ({ userId, id, exclude = [], kind }) =>
  ({ visible, columns }) => {
    const hiddenColumnsStorage = getStorage({ kind })

    const colIds = columns
      .map(({ colId }) => colId)
      .filter((id) => !exclude.includes(id))
    const userHiddenColumns = hiddenColumnsStorage[id] || []
    const hiddenColumns = visible
      ? userHiddenColumns.filter((col) => !colIds.includes(col))
      : [...userHiddenColumns, ...colIds]

    if (hiddenColumns.length > 0) {
      set(hiddenColumnsStorage, [id], hiddenColumns)
    } else {
      delete hiddenColumnsStorage[id]
      if (isEmpty(hiddenColumnsStorage[id])) {
        delete hiddenColumnsStorage[id]
      }
    }

    const userPref = userPreferencesStore.get() || {}

    const config = {
      [kind]: {
        ...userPref?.[kind],
        [HIDDEN_COLUMNS]: hiddenColumnsStorage
      }
    }

    updateUserDataPreferences({
      userId,
      config
    })
  }

const makeIsColumnHidden =
  ({ id, kind }) =>
  (colDef) => {
    const hiddenColumnsStorage = getStorage({ kind })
    const colId = adapters.getId(colDef)
    return hiddenColumnsStorage[id]?.includes(colId)
  }

export default function useColumnVisibility({ exclude, id, kind }) {
  const userId = getUserId()

  const methods = useMemo(() => {
    const params = { userId, exclude, id, kind }
    return {
      columnVisibilityHandler: makeColumnVisibilityHandler(params),
      isColumnHidden: makeIsColumnHidden(params)
    }
  }, [userId])

  return methods
}

const getUserId = () => {
  const userId = useStoreSelector('user', subUserId ?? selectUserId)
  return userId
}
